import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/cn'

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[auto_1fr] [&>svg~*]:col-start-2 gap-x-3 items-start [&>svg]:size-5 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-foreground border-border',
        destructive: 'bg-destructive/10 text-destructive-foreground border-destructive/30',
        success: 'bg-success/10 text-success border-success/30',
        warning: 'bg-warning/15 text-warning border-warning/40',
        info: 'bg-accent text-accent-foreground border-accent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role='alert' className={cn(alertVariants({ variant, className }))} {...props} />
))
Alert.displayName = 'Alert'

export type AlertTitleProps = React.HTMLAttributes<HTMLHeadingElement>

const AlertTitle = React.forwardRef<HTMLHeadingElement, AlertTitleProps>(({ className, ...props }, ref) => (
  <h5 ref={ref} className={cn('font-semibold leading-tight tracking-tight mb-1 last:mb-0', className)} {...props} />
))
AlertTitle.displayName = 'AlertTitle'

export type AlertDescriptionProps = React.HTMLAttributes<HTMLDivElement>

const AlertDescription = React.forwardRef<HTMLDivElement, AlertDescriptionProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm leading-relaxed opacity-90', className)} {...props} />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription, alertVariants }
